import React from "react"
import { useTranslation } from "react-i18next"

import { useFormatPersonName } from "@/formatters"
import type { User } from "@/models/user"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { useUsers } from "@/services/user/use-users"
import { useDebounce } from "@uidotdev/usehooks"

import SearchIcon from "@mui/icons-material/Search"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import InputAdornment from "@mui/material/InputAdornment"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Skeleton from "@mui/material/Skeleton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"

import type { UserSelectionDialogProps } from "./user-selection-dialog.types"

const itemHeight = 60
const minCharacterCount = 3

export const UserSelectionDialog: React.FC<UserSelectionDialogProps> = ({
  confirmText = "OK",
  description,
  onClose,
  onConfirm,
  open,
  title,
}) => {
  const { t } = useTranslation()
  const { formatPersonName } = useFormatPersonName()
  const [searchText, setSearchText] = React.useState("")
  const debouncedSearchText = useDebounce(searchText, 250)
  const [selectedUser, setSelectedUser] = React.useState<User | undefined>()
  const { usersData, usersIsFetching, usersIsPlaceholderData } = useUsers({
    apiQueryOptions: {
      pageSize: 50,
      sort: { first_name: "asc" },
      where: { first_name: debouncedSearchText },
    },
    queryOptions: {
      enabled: debouncedSearchText.length > 2,
    },
  })

  const reset = React.useCallback((): void => {
    setSearchText("")
    setSelectedUser(undefined)
  }, [setSearchText, setSelectedUser])

  const handleClose = React.useCallback((): void => {
    onClose()
    reset()
  }, [onClose, reset])

  const handleConfirm = React.useCallback((): void => {
    if (selectedUser) {
      onConfirm(selectedUser)
      reset()
    }
  }, [onConfirm, reset, selectedUser])

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value)
    },
    []
  )

  const handleListItemClick = (user: User): void => {
    setSelectedUser(user)
  }

  return (
    <Dialog
      aria-describedby="user-selection-dialog-description"
      aria-labelledby="user-selection-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
    >
      <DialogTitle id="user-selection-dialog-title" onClose={handleClose}>
        {title}
      </DialogTitle>
      <DialogContent>
        {description && (
          <Typography
            id="user-selection-dialog-description"
            sx={{ mb: 2, overflowY: "auto" }}
          >
            {description}
          </Typography>
        )}
        <TextField
          error={
            debouncedSearchText.length > 0 &&
            debouncedSearchText.length < minCharacterCount
          }
          fullWidth
          helperText={t("impersonateUser.searchMinMessage", { ns: "common" })}
          onChange={handleInputChange}
          placeholder={t("impersonateUser.searchPlaceholder", { ns: "common" })}
          slotProps={{
            htmlInput: {
              "aria-label": t("impersonateUser.searchLabel", { ns: "common" }),
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <List
          aria-label={t("impersonateUser.usersLabel", { ns: "common" })}
          sx={{
            height: `calc(${itemHeight}px * 4.5 + 8px)`,
            overflowY: "auto",
          }}
        >
          {debouncedSearchText.length >= minCharacterCount &&
            usersData?.length === 0 && (
              <ListItem key="-1">
                <ListItemText
                  primary={t("impersonateUser.searchNotFound", {
                    ns: "common",
                  })}
                />
              </ListItem>
            )}
          {usersData?.map((user) => {
            if (usersIsFetching && usersIsPlaceholderData) {
              return <Skeleton key={user.id} height={itemHeight} width="100%" />
            }

            if (!usersIsPlaceholderData) {
              return (
                <ListItemButton
                  key={user.id}
                  onClick={() => {
                    handleListItemClick(user)
                  }}
                  selected={user.id === selectedUser?.id}
                >
                  <ListItemText
                    primary={formatPersonName({
                      firstName: user.firstName,
                      lastName: user.lastName,
                    })}
                    secondary={user.email}
                  />
                </ListItemButton>
              )
            }
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t("button.cancel", { ns: "common" })}
        </Button>
        <Button
          disabled={!selectedUser}
          onClick={handleConfirm}
          variant="contained"
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
